/**
 * File: apps/qbench-cp/reactapp/src/context/orderRequest.selectors.js
 *
 * Purpose: Pure selector functions for filtering and grouping normalized test entities, extracted
 *     from OrderRequestV2Provider so they can be unit tested without the context's closure state.
 *
 * Scope: Order-request context — test filtering by assay/panel/panel-assay ID and grouping by
 *     sample and assay/panel ID.
 *
 * Tags: customer-portal, orders
 *
 * Overview: filterTestsByAssayId returns standalone (non-panel) tests matching an assay ID;
 *     filterTestsByPanelId returns all tests under a panel ID regardless of assay; and
 *     filterTestsByPanelAssayId returns tests matching both a panel ID and a child assay ID. All
 *     three guard against falsy IDs and empty inputs, and filter results to only IDs present in
 *     the caller-supplied work-in-progress test ID list so deleted-but-cached tests are excluded.
 *     groupTestsBySampleIdAssayPanelId groups a flat test array into sampleId -> (assayId or
 *     panelId, depending on isPanel) -> array of test IDs.
 *
 * Exports: filterTestsByAssayId, filterTestsByPanelId, filterTestsByPanelAssayId,
 *     groupTestsBySampleIdAssayPanelId.
 */

import { groupBy, isEmpty, mapValues } from 'lodash';

/**
 * Pure selector functions extracted from OrderRequestV2Provider for testability.
 * The context provider wraps these with closure state (entities, wip).
 */

export function filterTestsByAssayId(testsMap, wipTestIds, assayId) {
  if (!assayId || isEmpty(wipTestIds) || isEmpty(testsMap)) return [];
  return Object.values(testsMap).filter(
    (test) => test.assay === assayId && !test.panel && wipTestIds.includes(test.id)
  );
}

export function filterTestsByPanelId(testsMap, wipTestIds, panelId) {
  if (!panelId || isEmpty(wipTestIds) || isEmpty(testsMap)) return [];
  return Object.values(testsMap).filter(
    (test) => test.panel === panelId && wipTestIds.includes(test.id)
  );
}

export function filterTestsByPanelAssayId(testsMap, wipTestIds, panelId, assayId) {
  if (!panelId || !assayId || isEmpty(wipTestIds) || isEmpty(testsMap)) return [];
  return Object.values(testsMap).filter(
    (t) => t.panel === panelId && t.assay === assayId && wipTestIds.includes(t.id)
  );
}

export function groupTestsBySampleIdAssayPanelId(testArr = [], isPanel = false) {
  return mapValues(groupBy(testArr, 'sample_id'), (x) =>
    mapValues(groupBy(x, isPanel ? 'panel_id' : 'assay_id'), (y) =>
      y.map((z) => z.id)
    )
  );
}
